import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import {
  createDisasterRecoveryProposalInputSchema,
  DisasterRecoveryProposalAction,
  type CreateDisasterRecoveryProposalInput,
  toChecksumAddress,
} from '@mls-wallet/shared-types';
import denApi from '@/lib/api/denApi';
import { SECONDS_PER_DAY } from '@/lib/utils';
import type { ActiveDRSupportingProposal } from './use-disaster-recovery-settings';
import {
  type DisasterRecoveryProposal,
  type ServerDisasterRecoveryProposal,
  mapInitiateProposal,
} from '../../../types/disaster-recovery-proposal';

type CreateDisasterRecoveryProposalArg = {
  recoveryWalletAddress: string;
  timelockDays: number;
};

type CreateDRSupportingProposalArg = {
  disasterRecoveryId: string;
};

type CreateDRSupportingProposalResponse =
  NonNullable<ActiveDRSupportingProposal>['proposal'];
type CreateDRInitiateProposalResponse = DisasterRecoveryProposal & {
  alreadyExists?: boolean;
};

type CreateDRProposalAction =
  | typeof DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY
  | typeof DisasterRecoveryProposalAction.CANCEL_INITIALIZE_TX_RECOVERY
  | typeof DisasterRecoveryProposalAction.FINALIZE_INITIALIZE_TX_RECOVERY;

type CreateDRProposalArg =
  | CreateDisasterRecoveryProposalArg
  | CreateDRSupportingProposalArg;

type CreateDRProposalResponse =
  | CreateDRInitiateProposalResponse
  | CreateDRSupportingProposalResponse;

type CreateDRProposalMutationOptions = Omit<
  SWRMutationConfiguration<
    CreateDRProposalResponse,
    Error,
    string,
    CreateDRProposalArg
  >,
  'fetcher'
>;

type CreateInitiateDisasterRecoveryProposalResult = {
  proposal: DisasterRecoveryProposal;
  alreadyExists: boolean;
};

// Create endpoints return proposal metadata only; review aggregates are fetched
// separately from the list/settings hooks after creation.
function mapSupportingProposalForCreate(
  proposal: ServerDisasterRecoveryProposal
): CreateDRSupportingProposalResponse {
  return {
    id: proposal.id,
    status: proposal.status,
    validity: proposal.validity,
    createdByUserId: proposal.createdByUserId,
    createdByUserName: proposal.createdByUser.name,
    createdAt: proposal.createdAt,
  };
}

function getCreateDRProposalSWRKey(action: CreateDRProposalAction): string {
  switch (action) {
    case DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY:
      return 'admin/organization/disaster-recovery-proposals';
    case DisasterRecoveryProposalAction.CANCEL_INITIALIZE_TX_RECOVERY:
      return 'admin/organization/disaster-recovery-proposals/cancel/create';
    case DisasterRecoveryProposalAction.FINALIZE_INITIALIZE_TX_RECOVERY:
      return 'admin/organization/disaster-recovery-proposals/finalize/create';
  }
}

function isInitiationArg(
  arg: CreateDRProposalArg
): arg is CreateDisasterRecoveryProposalArg {
  return 'recoveryWalletAddress' in arg && 'timelockDays' in arg;
}

const createDRProposalFetcher =
  (action: CreateDRProposalAction) =>
  async (
    _key: string,
    { arg }: { arg: CreateDRProposalArg }
  ): Promise<CreateDRProposalResponse> => {
    if (
      action === DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY
    ) {
      if (!isInitiationArg(arg)) {
        throw new Error(
          'INITIATE proposal requires recoveryWalletAddress and timelockDays'
        );
      }

      const body: CreateDisasterRecoveryProposalInput =
        createDisasterRecoveryProposalInputSchema.parse({
          action:
            DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY,
          recoveryAddress: toChecksumAddress(arg.recoveryWalletAddress.trim()),
          timelockDurationSeconds: arg.timelockDays * SECONDS_PER_DAY,
        });

      const created = await denApi.post<
        ServerDisasterRecoveryProposal,
        CreateDisasterRecoveryProposalInput
      >(`admin/organization/disaster-recovery-proposals`, body);
      return {
        ...mapInitiateProposal(created),
        alreadyExists: created.alreadyExists === true,
      };
    }

    if (!('disasterRecoveryId' in arg)) {
      throw new Error('CANCEL/FINALIZE proposal requires disasterRecoveryId');
    }

    const body: CreateDisasterRecoveryProposalInput =
      createDisasterRecoveryProposalInputSchema.parse({
        action,
        disasterRecoveryId: arg.disasterRecoveryId,
      });

    const created = await denApi.post<
      ServerDisasterRecoveryProposal,
      CreateDisasterRecoveryProposalInput
    >(`admin/organization/disaster-recovery-proposals`, body);

    return mapSupportingProposalForCreate(created);
  };

/**
 * Creates a DR proposal by action:
 * - INITIATE requires recoveryWalletAddress + timelockDays
 * - CANCEL/FINALIZE require disasterRecoveryId
 */
export function useCreateDRProposal(
  action: CreateDRProposalAction,
  swrOptions?: CreateDRProposalMutationOptions
) {
  const swrKey = getCreateDRProposalSWRKey(action);
  const mutation = useSWRMutation<
    CreateDRProposalResponse,
    Error,
    string,
    CreateDRProposalArg
  >(swrKey, createDRProposalFetcher(action), swrOptions);

  return {
    createProposal: mutation.trigger,
    isLoading: mutation.isMutating,
    error: mutation.error,
  };
}

/**
 * Compatibility wrapper for initiation proposal call sites.
 * Prefer `useCreateDRProposal(INITIATE_...)` for new usage.
 */
export function useCreateDisasterRecoveryProposal(
  swrOptions?: CreateDRProposalMutationOptions
) {
  const generic = useCreateDRProposal(
    DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY,
    swrOptions
  );

  const createProposal = async (
    arg: CreateDisasterRecoveryProposalArg
  ): Promise<CreateInitiateDisasterRecoveryProposalResult> => {
    const created = await generic.createProposal(arg);
    if (!('timelockDurationSeconds' in created)) {
      throw new Error('Expected INITIATE disaster recovery proposal response');
    }
    return {
      proposal: created,
      alreadyExists:
        'alreadyExists' in created && created.alreadyExists === true,
    };
  };

  return {
    createProposal,
    isLoading: generic.isLoading,
    error: generic.error,
  };
}
